/**
 * P_RECOGNITION_LIFECYCLE_6.0e — Recognition admin page.
 *
 * Single role-gated page at /admin/recognition. Combines:
 *   • Scheduler Status card     (read-only — from 6.0a.1 health endpoint)
 *   • Recognition Summary chips (from 6.0d summary block)
 *   • Filters: kind / from / to / employee search / refresh
 *   • Timeline table with per-row Skip + Defer actions (6.0c)
 *
 * No charts, no graphs, no calendar, no polling, no websockets. Manual
 * refresh only. data-testid coverage on every interactive element.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    Cake, Sparkles, Trophy, Activity, Filter, RefreshCw,
    SkipForward, Clock, AlertTriangle, ShieldOff, Loader2,
} from 'lucide-react';
import api from '../lib/api';
// P_USER_WELCOME_DISPATCH_2.1 — proper UTC→local conversion.
import { fmtLocalDateTime, fmtLocalShort } from '../utils/localTime';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

const KIND_LABELS = {
    all: 'All',
    birthday: 'Birthdays',
    anniversary: 'Anniversaries',
    milestone: 'Milestones',
};

const KIND_ICONS = {
    birthday: Cake,
    anniversary: Sparkles,
    milestone: Trophy,
};

const STATUS_BADGE = {
    scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    sending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    sent: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    cancelled: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
    deferred: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
};

function StatusBadge({ status }) {
    const cls = STATUS_BADGE[status] || 'bg-slate-100 text-slate-800 dark:bg-slate-900/40 dark:text-slate-300';
    return (
        <span data-testid={`row-status-${status}`}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
            {status}
        </span>
    );
}

function KindBadge({ kind }) {
    const Icon = KIND_ICONS[kind] || Activity;
    return (
        <span data-testid={`row-kind-${kind}`}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-200">
            <Icon className="w-3 h-3" />
            {kind}
        </span>
    );
}

function SummaryChip({ label, value, active, onClick, testid, Icon }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition border ${
                active
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-card text-foreground border-border hover:bg-muted'
            }`}>
            {Icon ? <Icon className="w-4 h-4" /> : null}
            <span>{label}</span>
            <span className={`tabular-nums ${active ? 'opacity-80' : 'text-muted-foreground'}`}>
                {value}
            </span>
        </button>
    );
}

export default function AdminRecognition() {
    const { t, i18n } = useTranslation();

    // ─── Filters ────────────────────────────────────────────────
    const [kind, setKind] = useState('all');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [employeeId, setEmployeeId] = useState('');

    // ─── Data ───────────────────────────────────────────────────
    const [data, setData] = useState({ summary: { total: 0, birthday: 0, anniversary: 0, milestone: 0 }, timeline: [] });
    const [loading, setLoading] = useState(true);
    const [health, setHealth] = useState(null);

    // ─── Modal state ────────────────────────────────────────────
    const [skipModal, setSkipModal] = useState({ open: false, row: null, reason: '' });
    const [deferModal, setDeferModal] = useState({ open: false, row: null, days: 7, reason: '' });
    const [acting, setActing] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = { kind, limit: 100 };
            if (from) params.from = from;
            if (to) params.to = to;
            if (employeeId.trim()) params.employee_id = employeeId.trim();
            const [tl, hl] = await Promise.all([
                api.get('/recognition/timeline', { params }),
                api.get('/recognition/scheduler/health').catch(() => ({ data: null })),
            ]);
            setData(tl.data);
            setHealth(hl.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [kind, from, to, employeeId]);

    useEffect(() => { reload(); }, [reload]);

    const todayProcessedTotal = useMemo(() => {
        if (!health) return 0;
        const t = health.today_processed || {};
        return (t.birthdays || 0) + (t.anniversaries || 0) + (t.milestones || 0);
    }, [health]);

    const onSkipConfirm = async () => {
        if (!skipModal.reason.trim()) {
            toast.error(t('recognition.skipReasonRequired', 'Reason is required.'));
            return;
        }
        setActing(true);
        try {
            await api.post(`/recognition/campaigns/${skipModal.row.campaign_id}/skip`,
                { reason: skipModal.reason.trim() });
            toast.success(t('recognition.skipped', 'Campaign skipped.'));
            setSkipModal({ open: false, row: null, reason: '' });
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Skip failed');
        } finally { setActing(false); }
    };

    const onDeferConfirm = async () => {
        const d = parseInt(deferModal.days, 10);
        if (!d || d < 1 || d > 365) {
            toast.error(t('recognition.deferDaysRange', 'Days must be between 1 and 365.'));
            return;
        }
        if (!deferModal.reason.trim()) {
            toast.error(t('recognition.deferReasonRequired', 'Reason is required.'));
            return;
        }
        setActing(true);
        try {
            await api.post(`/recognition/campaigns/${deferModal.row.campaign_id}/defer`,
                { days: d, reason: deferModal.reason.trim() });
            toast.success(t('recognition.deferred', 'Campaign deferred.'));
            setDeferModal({ open: false, row: null, days: 7, reason: '' });
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Defer failed');
        } finally { setActing(false); }
    };

    const schedulerOn = !!(health && health.scheduler_enabled);

    return (
        <div className="space-y-6 p-4 max-w-7xl mx-auto" data-testid="admin-recognition-page">
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">
                        {t('recognition.title', 'Recognition')}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {t('recognition.subtitle', 'Birthdays, anniversaries, milestones.')}
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={reload}
                    disabled={loading} data-testid="refresh-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    <span className="ml-2">{t('recognition.refresh', 'Refresh')}</span>
                </Button>
            </header>

            {/* ── Scheduler Status ─────────────────────────────── */}
            <Card className="p-4" data-testid="scheduler-status-card">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        {schedulerOn ? (
                            <Activity className="w-5 h-5 text-emerald-600" />
                        ) : (
                            <ShieldOff className="w-5 h-5 text-amber-600" />
                        )}
                        <div>
                            <div className="font-medium text-foreground" data-testid="scheduler-state">
                                {schedulerOn
                                    ? t('recognition.schedulerOn', 'Scheduler ON')
                                    : t('recognition.schedulerOff', 'Scheduler OFF (safe mode)')}
                            </div>
                            <div className="text-xs text-muted-foreground" data-testid="scheduler-last-tick">
                                {t('recognition.lastTick', 'Last tick')}: {fmtLocalDateTime(health?.last_tick_at, i18n.language) || '—'}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                            <div className="text-xs text-muted-foreground">{t('recognition.todayProcessed', 'Today')}</div>
                            <div className="font-medium tabular-nums" data-testid="scheduler-today-processed">{todayProcessedTotal}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-xs text-muted-foreground">{t('recognition.totalShown', 'Shown')}</div>
                            <div className="font-medium tabular-nums" data-testid="scheduler-total-shown">{data.summary.total}</div>
                        </div>
                    </div>
                </div>
                {!schedulerOn ? (
                    <div className="mt-3 flex items-start gap-2 rounded-md bg-white dark:bg-slate-900/40 border p-2 text-xs text-slate-700 dark:text-slate-200"
                        style={{ borderColor: 'rgba(245,130,32,0.45)' }}
                        data-testid="scheduler-disabled-banner">
                        <AlertTriangle className="w-4 h-4 mt-0.5" />
                        <span>
                            {t('recognition.schedulerDisabledBanner',
                                'Recognition scheduler is currently disabled. Campaigns will not be created until scheduling is enabled.')}
                        </span>
                    </div>
                ) : null}
            </Card>

            {/* ── Summary Chips ────────────────────────────────── */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="summary-chips">
                <SummaryChip testid="chip-all" label={KIND_LABELS.all}
                    value={data.summary.total} active={kind === 'all'} onClick={() => setKind('all')} />
                <SummaryChip testid="chip-birthday" label={KIND_LABELS.birthday}
                    Icon={Cake} value={data.summary.birthday}
                    active={kind === 'birthday'} onClick={() => setKind('birthday')} />
                <SummaryChip testid="chip-anniversary" label={KIND_LABELS.anniversary}
                    Icon={Sparkles} value={data.summary.anniversary}
                    active={kind === 'anniversary'} onClick={() => setKind('anniversary')} />
                <SummaryChip testid="chip-milestone" label={KIND_LABELS.milestone}
                    Icon={Trophy} value={data.summary.milestone}
                    active={kind === 'milestone'} onClick={() => setKind('milestone')} />
            </div>

            {/* ── Filters ──────────────────────────────────────── */}
            <Card className="p-4" data-testid="filters-card">
                <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                    <Filter className="w-4 h-4" />
                    {t('recognition.filtersTitle', 'Filters')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <Label className="text-xs">{t('recognition.kind', 'Kind')}</Label>
                        <Select value={kind} onValueChange={setKind}>
                            <SelectTrigger data-testid="filter-kind"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(KIND_LABELS).map(([k, label]) => (
                                    <SelectItem key={k} value={k} data-testid={`filter-kind-opt-${k}`}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">{t('recognition.from', 'From')}</Label>
                        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                            data-testid="filter-from" />
                    </div>
                    <div>
                        <Label className="text-xs">{t('recognition.to', 'To')}</Label>
                        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                            data-testid="filter-to" />
                    </div>
                    <div>
                        <Label className="text-xs">{t('recognition.employeeId', 'Employee ID')}</Label>
                        <Input value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                            placeholder={t('recognition.employeeIdPlaceholder', 'Optional')}
                            data-testid="filter-employee-id" />
                    </div>
                </div>
            </Card>

            {/* ── Timeline Table ───────────────────────────────── */}
            <Card className="p-0 overflow-hidden" data-testid="timeline-card">
                {loading ? (
                    <div className="p-8 text-center text-muted-foreground" data-testid="timeline-loading">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    </div>
                ) : data.timeline.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground" data-testid="timeline-empty">
                        {t('recognition.empty', 'No recognition events found.')}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="timeline-table">
                            <thead className="bg-muted/40">
                                <tr className="text-left">
                                    <th className="px-4 py-2 font-medium">{t('recognition.col.employee', 'Employee')}</th>
                                    <th className="px-4 py-2 font-medium">{t('recognition.col.type', 'Type')}</th>
                                    <th className="px-4 py-2 font-medium">{t('recognition.col.scheduled', 'Scheduled')}</th>
                                    <th className="px-4 py-2 font-medium">{t('recognition.col.status', 'Status')}</th>
                                    <th className="px-4 py-2 font-medium text-right">{t('recognition.col.actions', 'Actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {data.timeline.map((row) => {
                                    const canAct = ['scheduled', 'draft'].includes(row.status) || row.status === null;
                                    return (
                                        <tr key={row.campaign_id}
                                            data-testid={`timeline-row-${row.campaign_id}`}>
                                            <td className="px-4 py-2 align-top">
                                                <div className="font-medium text-foreground" data-testid={`row-employee-name-${row.campaign_id}`}>
                                                    {row.employee_name || '—'}
                                                </div>
                                                <div className="text-xs text-muted-foreground font-mono">
                                                    {(row.employee_id || '').slice(0, 8)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 align-top"><KindBadge kind={row.kind} /></td>
                                            <td className="px-4 py-2 align-top tabular-nums text-xs">
                                                {fmtLocalShort(row.scheduled_for, i18n.language) || '—'}
                                            </td>
                                            <td className="px-4 py-2 align-top"><StatusBadge status={row.status} /></td>
                                            <td className="px-4 py-2 align-top text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button size="sm" variant="outline"
                                                        disabled={!canAct}
                                                        onClick={() => setSkipModal({ open: true, row, reason: '' })}
                                                        data-testid={`row-skip-btn-${row.campaign_id}`}>
                                                        <SkipForward className="w-3.5 h-3.5 mr-1" />
                                                        {t('recognition.skip', 'Skip')}
                                                    </Button>
                                                    <Button size="sm" variant="outline"
                                                        disabled={!canAct}
                                                        onClick={() => setDeferModal({ open: true, row, days: 7, reason: '' })}
                                                        data-testid={`row-defer-btn-${row.campaign_id}`}>
                                                        <Clock className="w-3.5 h-3.5 mr-1" />
                                                        {t('recognition.defer', 'Defer')}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* ── Skip Modal ───────────────────────────────────── */}
            <Dialog open={skipModal.open} onOpenChange={(o) => !o && setSkipModal({ open: false, row: null, reason: '' })}>
                <DialogContent data-testid="skip-modal">
                    <DialogHeader>
                        <DialogTitle>{t('recognition.skipTitle', 'Skip recognition?')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                            {t('recognition.skipReasonHint', 'Reason is required. The campaign will be cancelled and never dispatched.')}
                        </p>
                        <Label className="text-xs">{t('recognition.reason', 'Reason')}</Label>
                        <Textarea value={skipModal.reason}
                            onChange={(e) => setSkipModal((s) => ({ ...s, reason: e.target.value }))}
                            rows={3} data-testid="skip-reason" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSkipModal({ open: false, row: null, reason: '' })}
                            data-testid="skip-cancel">{t('recognition.cancel', 'Cancel')}</Button>
                        <Button onClick={onSkipConfirm} disabled={acting}
                            data-testid="skip-confirm">
                            {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            {t('recognition.skip', 'Skip')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Defer Modal ──────────────────────────────────── */}
            <Dialog open={deferModal.open} onOpenChange={(o) => !o && setDeferModal({ open: false, row: null, days: 7, reason: '' })}>
                <DialogContent data-testid="defer-modal">
                    <DialogHeader>
                        <DialogTitle>{t('recognition.deferTitle', 'Defer recognition')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">{t('recognition.days', 'Days to defer')}</Label>
                            <Input type="number" min={1} max={365} value={deferModal.days}
                                onChange={(e) => setDeferModal((s) => ({ ...s, days: e.target.value }))}
                                data-testid="defer-days" />
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('recognition.deferRange', '1–365 days.')}
                            </p>
                        </div>
                        <div>
                            <Label className="text-xs">{t('recognition.reason', 'Reason')}</Label>
                            <Textarea value={deferModal.reason}
                                onChange={(e) => setDeferModal((s) => ({ ...s, reason: e.target.value }))}
                                rows={3} data-testid="defer-reason" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeferModal({ open: false, row: null, days: 7, reason: '' })}
                            data-testid="defer-cancel">{t('recognition.cancel', 'Cancel')}</Button>
                        <Button onClick={onDeferConfirm} disabled={acting}
                            data-testid="defer-confirm">
                            {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            {t('recognition.defer', 'Defer')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
